import React, { Dispatch, SetStateAction } from 'react'
import {
  Badge,
  Button,
  CheckboxGroup,
  Collapse,
  Panel,
  theme,
} from '@lmc/ui-kit'
import styled from 'styled-components'
import { Carer, UserRole } from '@lmc/ts-common'
import { getCarerRole } from '../utils'
import { Home } from '@lmc/ts-common'
import { useSelector } from 'react-redux'

type CheckboxValueType = string | number | boolean

export type TeamSideFiltersGroupsProps = {
  carer?: Carer
  onClick?: () => void
  onStatuses: Dispatch<SetStateAction<string[]>>
  onRoles: Dispatch<SetStateAction<UserRole[]>>
  onSites: Dispatch<SetStateAction<string[]>>
  statuses: string[]
  roles: string[]
  features: Home
  sites: string[]
  clearButtonTitle: string
  statusTitle?: string
  roleTitle: string
  siteTitle: string
  onDefaultOpened?: boolean
  viewProTitle?: string
  onSubscribe?: () => void
  isGroupUser?: boolean
}

const StyledCollapse = styled(Collapse)`
  background-color: ${(props) => props.theme.color.neutral.color10} !important;
  border-radius: 8px;
`

const StyledExtraWrapper = styled.div`
  display: flex;
`

const StyledBadge = styled(Badge)`
  width: 24px;
  height: 24px;
  border-radius: 12px;
  background-color: red;
`

const StyledWrapper = styled.div`
  max-width: 345px;
  width: 100%;
  margin-top: 24px;
  padding-left: 24px;
  overflow-y: auto;
  height: calc(100vh - 210px);
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    margin-bottom: 24px;
    max-width: unset;
    padding-right: 24px;
  }
`

const StyledCheckboxGroup = styled(CheckboxGroup)`
  display: flex !important;
  flex-direction: column !important;

  .ant-checkbox-wrapper {
    margin-left: 0px;
  }
`

const TeamSideGroupsFilters: React.FC<TeamSideFiltersGroupsProps> = ({
  onStatuses,
  onRoles,
  onSites,
  statuses,
  roles,
  sites,
  clearButtonTitle,
  statusTitle,
  roleTitle,
  siteTitle,
  isGroupUser,
}) => {
  const onChangeStatus = (checkedValues: CheckboxValueType[]) =>
    onStatuses(checkedValues as string[])

  const onChangeRole = (checkedValues: CheckboxValueType[]) =>
    onRoles(checkedValues as UserRole[])

  const onChangeSites = (checkedValues: CheckboxValueType[]) =>
    onSites(checkedValues as string[])

  const getExtra = (
    values: string[],
    onClear: Dispatch<SetStateAction<string[]>>
  ) => {
    const hasValues = !!values?.length

    return (
      <StyledExtraWrapper>
        {hasValues && (
          <Button
            variant="text"
            onClick={(e) => {
              e.stopPropagation()
              onClear([])
            }}
          >
            {clearButtonTitle}
          </Button>
        )}
        <StyledBadge
          size="small"
          text={values?.length.toString()}
          color={
            hasValues ? theme.color.status.primary : theme.color.neutral.color8
          }
          textColor={
            !hasValues
              ? theme.color.neutral.color1
              : theme.color.neutral.color11
          }
          style={{
            height: 24,
            width: 24,
            padding: '4px 0px 0px 9.5px',
            marginLeft: 5,
            paddingRight: 5,
          }}
        />
      </StyledExtraWrapper>
    )
  }

  const homes = useSelector(
    (state: { data: { homes: Home[] } }) => state.data.homes
  )

  const homeOptions = homes?.map((home) => ({
    value: home?.id,
    label: home?.name,
  }))

  let roleOptions = [
    {
      value: 'carehome-admin',
      label: getCarerRole(UserRole.careHomeAdmin),
    },
    { value: 'carer', label: getCarerRole(UserRole.carer) },
    {
      value: 'senior-carer',
      label: getCarerRole(UserRole.seniorCarer),
    },
  ]

  if (isGroupUser) {
    roleOptions = [
      ...roleOptions,
      {
        value: UserRole.groupAdmin,
        label: getCarerRole(UserRole.groupAdmin),
      },
      {
        value: UserRole.groupManager,
        label: getCarerRole(UserRole.groupManager),
      },
    ]
  }

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
  ]

  return (
    <StyledWrapper>
      <StyledCollapse expandIconPosition="start" defaultActiveKey={['1']}>
        <Panel
          header={statusTitle}
          key="1"
          extra={getExtra(statuses, onStatuses)}
        >
          <StyledCheckboxGroup
            onChange={onChangeStatus}
            options={statusOptions}
            value={statuses}
          />
        </Panel>
        <Panel
          header={roleTitle}
          key="2"
          extra={getExtra(
            roles,
            onRoles as unknown as Dispatch<SetStateAction<string[]>>
          )}
        >
          <StyledCheckboxGroup
            onChange={onChangeRole}
            value={roles}
            options={roleOptions}
          />
        </Panel>
        {isGroupUser && (
          <Panel header={siteTitle} key="3" extra={getExtra(sites, onSites)}>
            <StyledCheckboxGroup
              onChange={onChangeSites}
              value={sites}
              options={homeOptions}
            />
          </Panel>
        )}
      </StyledCollapse>
    </StyledWrapper>
  )
}

export default TeamSideGroupsFilters
